// Pure builder — given a tenant theme + light/dark mode, returns the CSS variable
// declarations to inject into <style id="tenant-theme">. No DOM, no React.
// Falls back to DEFAULT_TENANT_THEME values when fields are missing or invalid.
//
// NO declares tokens de ESTADO aquí (success/warning/info/destructive). No son
// configurables por tenant, así que una copia en este fichero solo puede hacer
// una cosa: separarse de `index.css` en silencio.
//
// Ya pasó. `--destructive: 0 72% 51%` estuvo escrito a mano justo aquí, y por dos
// motivos a la vez:
//
//  1. Se quedó en el valor viejo cuando se recalibró el token (erp#2521), así que
//     el arreglo de contraste no llegó a la pantalla. La línea base de
//     accesibilidad salió IDÉNTICA hasta el segundo decimal y parecía que el
//     arreglo no servía. Lo que no servía era mirar solo `index.css`.
//  2. Este bloque se inyecta en `:root` DESPUÉS de `index.css`, así que ganaba
//     también sobre `.dark { --destructive }`: en tema oscuro se estaba pintando
//     el rojo del tema claro.
//
// Lo vigila `buildThemeCss.test.js`.

import { ACCENTS, DENSITIES, DEFAULT_TENANT_THEME, NEUTRAL } from '../theme/tokens';

function pickAccent(theme) {
  // Priority: theme.primary (h,s,l literal) → theme.accent (named preset) → indigo.
  if (
    theme?.primary &&
    Number.isFinite(theme.primary.h) &&
    Number.isFinite(theme.primary.s) &&
    Number.isFinite(theme.primary.l)
  ) {
    return { h: theme.primary.h, s: theme.primary.s, l: theme.primary.l };
  }
  const named = ACCENTS[theme?.accent];
  return named || ACCENTS.indigo;
}

function pickDensity(name) {
  return DENSITIES[name] || DENSITIES.cozy;
}

function pickRadius(value) {
  return /^\d+(\.\d+)?(rem|px|em)$/.test(value || '') ? value : DEFAULT_TENANT_THEME.radius;
}

export function buildThemeCss(theme, mode = 'light') {
  const palette = mode === 'dark' ? NEUTRAL.dark : NEUTRAL.light;
  const a = pickAccent(theme);
  const d = pickDensity(theme?.density);
  const radius = pickRadius(theme?.radius);

  return `:root {
  --background: ${palette.background};
  --foreground: ${palette.foreground};
  --card: ${palette.card};
  --card-foreground: ${palette.cardForeground};
  --popover: ${palette.card};
  --popover-foreground: ${palette.cardForeground};
  --primary: ${a.h} ${a.s}% ${a.l}%;
  --primary-h: ${a.h};
  --primary-s: ${a.s}%;
  --primary-foreground: 0 0% 99%;
  --secondary: ${palette.muted};
  --secondary-foreground: ${palette.foreground};
  --muted: ${palette.muted};
  --muted-foreground: ${palette.mutedForeground};
  --accent: ${palette.muted};
  --accent-foreground: ${palette.foreground};
  --border: ${palette.border};
  --input: ${palette.input};
  --ring: ${a.h} ${a.s}% ${a.l}%;
  --sidebar-background: ${palette.sidebarBackground};
  --sidebar-foreground: ${palette.sidebarForeground};
  --sidebar-border: ${palette.sidebarBorder};
  --sidebar-accent: ${palette.muted};
  --sidebar-accent-foreground: ${palette.foreground};
  --sidebar-primary: ${a.h} ${a.s}% ${a.l}%;
  --sidebar-primary-foreground: 0 0% 99%;
  --sidebar-ring: ${a.h} ${a.s}% ${a.l}%;
  --radius: ${radius};
  --density-row: ${d.rowH}px;
  --density-pad: ${d.pad}px;
  --density-gap: ${d.gap}px;
  --density-base: ${d.fontBase}px;
  --chart-1: ${a.h} ${a.s}% ${a.l}%;
  --chart-2: ${(a.h + 40) % 360} 60% 50%;
  --chart-3: ${(a.h + 80) % 360} 55% 50%;
  --chart-4: ${(a.h + 200) % 360} 60% 55%;
  --chart-5: ${(a.h + 280) % 360} 60% 55%;
}`;
}
